import type { RaspadinhaDayWinningSchedule } from '@/types/raspadinha-campaign'
import {
  addGeneratedWinningTimeSlots,
  formatCampaignDateLabel,
  isValidWinningTime,
  type GeneratedWinningTimeSlotInput,
} from './date-winning-schedules'
import {
  generateRandomWinningTimeSlots,
  MAX_AUTO_GENERATED_WINNERS,
  MIN_AUTO_GENERATED_WINNERS,
} from './generate-random-winning-times'

export type CampaignWideSlotsByDate = Partial<Record<string, GeneratedWinningTimeSlotInput[]>>

export type CampaignWideSlotDistribution = {
  date: string
  count: number
}

export type CampaignWideGenerationPreview = {
  slotsByDate: CampaignWideSlotsByDate
  distribution: CampaignWideSlotDistribution[]
  totalSlots: number
  contemplatedPrizeId: string
}

export type GenerateCampaignWideWinningTimeSlotsInput = {
  schedules: RaspadinhaDayWinningSchedule[]
  totalSlotCount: number
  startTime: string
  endTime: string
  contemplatedPrizeId: string
}

export type GenerateCampaignWideWinningTimeSlotsResult =
  | { ok: true; preview: CampaignWideGenerationPreview }
  | { ok: false; error: string }

/** Divide a quantidade total em partes iguais entre as datas (resto nos primeiros dias). */
export function distributeEqualCountsAcrossDates(
  totalCount: number,
  dates: readonly string[]
): CampaignWideSlotDistribution[] {
  const normalizedTotal = Math.trunc(totalCount)

  if (dates.length === 0 || normalizedTotal < 1) {
    return []
  }

  const baseCount = Math.floor(normalizedTotal / dates.length)
  const remainder = normalizedTotal % dates.length

  return dates.map((date, index) => ({
    date,
    count: baseCount + (index < remainder ? 1 : 0),
  }))
}

function listOccupiedTimesForDate(
  schedules: RaspadinhaDayWinningSchedule[],
  date: string
): string[] {
  const daySchedule = schedules.find((schedule) => schedule.date === date)

  return (
    daySchedule?.time_slots
      .map((slot) => slot.time)
      .filter((time) => isValidWinningTime(time)) ?? []
  )
}

export function generateCampaignWideWinningTimeSlots(
  input: GenerateCampaignWideWinningTimeSlotsInput,
  random: () => number = Math.random
): GenerateCampaignWideWinningTimeSlotsResult {
  const totalSlotCount = Math.trunc(input.totalSlotCount)
  const contemplatedPrizeId = input.contemplatedPrizeId.trim()
  const dates = input.schedules.map((schedule) => schedule.date)

  if (dates.length === 0) {
    return {
      ok: false,
      error: 'Defina o período da campanha para gerar horários em todos os dias.',
    }
  }

  if (!Number.isFinite(totalSlotCount) || totalSlotCount < MIN_AUTO_GENERATED_WINNERS) {
    return {
      ok: false,
      error: `Informe pelo menos ${MIN_AUTO_GENERATED_WINNERS} horário para distribuir na campanha.`,
    }
  }

  const maxTotalSlots = dates.length * MAX_AUTO_GENERATED_WINNERS
  if (totalSlotCount > maxTotalSlots) {
    return {
      ok: false,
      error: `A quantidade máxima permitida é ${maxTotalSlots} horários para ${dates.length} dia(s) da campanha.`,
    }
  }

  if (!contemplatedPrizeId) {
    return {
      ok: false,
      error: 'Selecione o prêmio que será contemplado nos horários gerados.',
    }
  }

  const distribution = distributeEqualCountsAcrossDates(totalSlotCount, dates)
  const slotsByDate: CampaignWideSlotsByDate = {}
  let generatedTotal = 0

  for (const [index, entry] of distribution.entries()) {
    if (entry.count < 1) {
      continue
    }

    const dayResult = generateRandomWinningTimeSlots(
      {
        winnerCount: entry.count,
        startTime: input.startTime,
        endTime: input.endTime,
        occupiedTimes: listOccupiedTimesForDate(input.schedules, entry.date),
        contemplatedPrizeId,
      },
      random
    )

    if (!dayResult.ok) {
      const dayLabel = formatCampaignDateLabel(entry.date, index + 1)
      return {
        ok: false,
        error: `${dayLabel}: ${dayResult.error}`,
      }
    }

    slotsByDate[entry.date] = dayResult.slots
    generatedTotal += dayResult.slots.length
  }

  return {
    ok: true,
    preview: {
      slotsByDate,
      distribution: distribution.filter((entry) => entry.count > 0),
      totalSlots: generatedTotal,
      contemplatedPrizeId,
    },
  }
}

export function applyCampaignWideGeneratedSlots(
  schedules: RaspadinhaDayWinningSchedule[],
  slotsByDate: CampaignWideSlotsByDate
): RaspadinhaDayWinningSchedule[] {
  return Object.entries(slotsByDate).reduce<RaspadinhaDayWinningSchedule[]>(
    (currentSchedules, [date, slots]) => {
      if (!slots?.length) {
        return currentSchedules
      }

      return addGeneratedWinningTimeSlots(currentSchedules, date, slots)
    },
    schedules
  )
}

export function hasCampaignWidePendingSlots(preview: CampaignWideGenerationPreview | null): boolean {
  return preview !== null && preview.totalSlots > 0
}
